import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Percent, Info } from "lucide-react";
import { APRTradeoffCard } from "@/components/dashboard/APRTradeoffCard";

export interface APREditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  apr: number;
  onAprChange: (value: number) => void;
  onHowItWorks?: () => void;
  paymentWindowDays: number;
  pipelineInvoiceCount?: number;
  pipelineTotalAmount?: number;
  yourAcceptRate?: number;
  acceleratedCount?: number;
}

export function APREditModal({
  open,
  onOpenChange,
  apr,
  onAprChange,
  onHowItWorks,
  paymentWindowDays,
  pipelineInvoiceCount = 0,
  pipelineTotalAmount = 0,
  yourAcceptRate,
  acceleratedCount = 0,
}: APREditModalProps) {
  const [inputValue, setInputValue] = useState(apr.toFixed(1));

  useEffect(() => {
    setInputValue(apr.toFixed(1));
  }, [apr, open]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0.5 && numValue <= 100) {
      onAprChange(numValue);
    }
  };

  const handleInputBlur = () => {
    const numValue = parseFloat(inputValue);
    if (isNaN(numValue) || numValue < 0.5) {
      setInputValue(apr.toFixed(1));
    } else if (numValue > 100) {
      onAprChange(100);
      setInputValue("100.0");
    } else {
      onAprChange(numValue);
      setInputValue(numValue.toFixed(1));
    }
  };

  const handleSliderChange = (values: number[]) => {
    const newValue = values[0];
    onAprChange(newValue);
    setInputValue(newValue.toFixed(1));
  };

  const handleHowItWorks = () => {
    onOpenChange(false);
    onHowItWorks?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Percent className="w-5 h-5 text-primary" />
            Discount APR & trade-off
          </DialogTitle>
          <DialogDescription>
            Set the discount rate and see how it affects savings vs supplier acceptance
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* APR edit row */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-lg bg-muted/40 border border-border/60">
            <div className="flex-1">
              <p className="text-xs font-medium text-foreground mb-1">Discount APR</p>
              <p className="text-[11px] text-muted-foreground">
                Annual rate used for early payment discount offers
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Input
                type="text"
                value={inputValue}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                className="w-20 h-9 text-lg font-display font-bold text-primary text-right"
              />
              <span className="text-lg font-display font-bold text-primary">%</span>
            </div>
          </div>
          <Slider
            value={[Math.min(apr, 40)]}
            onValueChange={handleSliderChange}
            min={1}
            max={40}
            step={0.5}
            className="w-full"
          />
          <div className="flex justify-between text-[10px] text-muted-foreground">
            <span>1%</span>
            <span>20%</span>
            <span>40%</span>
          </div>
          {apr > 40 && (
            <p className="text-[10px] text-muted-foreground">
              Slider max is 40%. Use the input above for higher values.
            </p>
          )}

          {onHowItWorks && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full p-2.5 bg-accent/5 hover:bg-accent/10 justify-start gap-2 rounded-lg h-auto"
              onClick={handleHowItWorks}
            >
              <Info className="w-4 h-4 text-accent flex-shrink-0" />
              <span className="text-xs font-medium text-foreground">How it works</span>
            </Button>
          )}

          {/* Trade-off content (no card wrapper so it fits the modal) */}
          <APRTradeoffCard
            currentApr={apr}
            paymentWindowDays={paymentWindowDays}
            pipelineInvoiceCount={pipelineInvoiceCount}
            pipelineTotalAmount={pipelineTotalAmount}
            yourAcceptRate={yourAcceptRate}
            acceleratedCount={acceleratedCount}
            noCard
          />
        </div>

        <div className="flex justify-end pt-2">
          <Button onClick={() => onOpenChange(false)}>Done</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
